/**
 * UserMenu — Sidebar footer dropdown with account actions.
 *
 * Opens upward from the sidebar footer. Shows user avatar, name/email,
 * links to Account Settings, and Sign Out.
 */

import { useNavigate } from 'react-router-dom';
import { COPY } from '@/lib/copyTokens';
import { LogOut, Settings, ChevronsUpDown, Plug } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useAuth } from '@/contexts/AuthContext';
import { canAccessAdmin } from '@/lib/adsToolsAccess';

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

export default function UserMenu() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { userProfile } = useWorkspaceStore();
  // Reachable reconnect path: agency Google Ads (MCC) OAuth is managed in the
  // admin Integrations tab. Surface it here (role-gated) so it isn't URL-only.
  const canManageIntegrations = canAccessAdmin(userProfile);

  const handleSignOut = async () => {
    await signOut();
    useWorkspaceStore.getState().reset();
    if (DISABLE_AUTH) {
      navigate('/login?signed_out=1', { replace: true });
      return;
    }
    navigate('/login', { replace: true });
  };

  const initials = userProfile?.display_name
    ? userProfile.display_name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : userProfile?.email?.slice(0, 2).toUpperCase() ?? '??';

  const displayName = userProfile?.display_name || 'Account';
  const email = userProfile?.email || '';

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton className="h-auto py-2 w-full">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-[10px] bg-coral-light text-coral-dark">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-0.5 leading-none flex-1 group-data-[collapsible=icon]:hidden">
                <span className="text-sm font-medium truncate">
                  {displayName}
                </span>
                <span className="text-xs text-muted-foreground truncate max-w-[140px]">
                  {email}
                </span>
              </div>
              <ChevronsUpDown className="size-3.5 text-muted-foreground group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="start"
            className="w-[240px] bottom-[calc(100%+0.5rem)] top-auto mb-2"
          >
            {/* User info */}
            <DropdownMenuLabel className="flex items-center gap-2 px-2 py-2">
              <Avatar className="size-8">
                <AvatarFallback className="text-xs bg-coral-light text-coral-dark">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-medium truncate max-w-[170px]">
                  {displayName}
                </span>
                {email && (
                  <span className="text-xs text-muted-foreground truncate max-w-[170px]">
                    {email}
                  </span>
                )}
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            {/* Account Settings */}
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/account')}
            >
              <Settings className="size-4 text-muted-foreground" />
              <span>Account Settings</span>
            </DropdownMenuItem>

            {/* Admin · Integrations (reconnect agency Google Ads OAuth) */}
            {canManageIntegrations && (
              <DropdownMenuItem
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => navigate(COPY.adminPanel.path)}
              >
                <Plug className="size-4 text-muted-foreground" />
                <span>{COPY.adminPanel.label}</span>
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {/* Sign out */}
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer text-muted-foreground"
              onClick={handleSignOut}
            >
              <LogOut className="size-4" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
